import { IGuestApi, PlaceTicketOrderRequest } from "@greeter/api";
import { assert } from "@greeter/assert";
import { Payment, Quantity, TicketEvent, TicketProduct } from "@greeter/core";
import { parsify, stringify } from "@greeter/json";
import { Log } from "@greeter/log";
import { queries } from "@greeter/url";
import { isType } from "@greeter/util";
import { downloadReceiptAndTickets } from "./util";
import {
  createFetchMachine,
  FetchActor,
  FetchActorFields,
  invoke,
  unwrapData,
} from "@greeter/xstate-util";
import { Stripe, StripeElements } from "@stripe/stripe-js";
import { assign, emit, raise, setup } from "xstate";
import { $ZodErrorTree } from "zod/v4/core";
import z from "zod";

function hasErrors(t: $ZodErrorTree<UserForm>): boolean {
  if (!("properties" in t)) return t.errors.length > 0;
  for (const v of Object.values(t.properties ?? {})) {
    if (v.errors.length > 0) return true;
  }
  return false;
}

function interact({
  context,
  event,
}: {
  context: EventMachineContext;
  event: EventMachineEvents;
}): Partial<EventMachineContext> {
  Log.debug("[interact] running interact!", { event });
  if (!isType(event, "set.firstName", "set.lastName", "set.email"))
    return context;

  const initialInteractionField: UserFormInitialInteraction | undefined =
    isType(event, "set.firstName")
      ? "firstName"
      : isType(event, "set.lastName")
      ? "lastName"
      : "email";

  const initialInteractions = new Set(context.initialInteractions);
  initialInteractions.add(initialInteractionField);

  Log.debug("[interact] running interact!", {
    event,
    initialInteractionField,
    initialInteractions,
  });

  return { initialInteractions };
}

export const UserFormSchema = z.object({
  firstName: z.string().min(1, { error: "Mangler dit fornavn" }),
  lastName: z.string().min(1, { error: "Mangler dit efternavn" }),
  email: z.email({ error: "Ugyldig email" }),
});
export type UserForm = z.infer<typeof UserFormSchema>;
export type UserFormInitialInteraction = keyof UserForm;

export type EventMachineApiRequirements = Pick<
  IGuestApi,
  | "fetchTicketEvent"
  | "placeTicketOrder"
  | "fetchTicketOrder"
  | "fetchTicketOrderReceipt"
  | "resendTicketOrderReceipt"
  | "createPayment"
  | "updateCustomer"
>;

type EventMachineContext = {
  slug: string;
  quantity: Quantity;

  userForm: UserForm;

  payment?: Payment;
  /**
   * Used to
   */
  initialInteractions: Set<UserFormInitialInteraction>;
  userFormErrors: $ZodErrorTree<UserForm>;

  event?: TicketEvent;
  api: FetchActorFields<EventMachineApiRequirements> & {
    checkPaymentRef: FetchActor<() => Promise<void>>;
  };

  stripe?: Pick<Stripe, "confirmPayment" | "retrievePaymentIntent">;
  stripeElements?: StripeElements;
};

type EventMachineInput = {
  slug: string;
  api: EventMachineApiRequirements;
  stripe: Promise<Stripe | null>;
};

type EventMachineEvents =
  | {
      type: "set.quantity";
      data: Quantity;
    }
  | { type: "incrementQuantity" }
  | { type: "decrementQuantity" }
  | { type: "forceFirstInteraction" }
  | { type: "downloadReceipt" }
  | { type: "resendReceipt" }
  | { type: "set.firstName"; data: string }
  | { type: "set.lastName"; data: string }
  | { type: "set.email"; data: string }
  | { type: "set.payment"; data?: Payment }
  | { type: "validate.userForm" }
  | { type: "validate.userForm.done" }
  | { type: "internal.setTicketEvent"; data: TicketEvent }
  | { type: "internal.setTicketProduct"; data: TicketProduct }
  | { type: "set.stripe"; data?: Stripe }
  | { type: "set.stripeElements"; data?: StripeElements }
  | { type: "goto.detail" }
  | { type: "goto.cart" }
  | { type: "placeTicketOrder" }
  | { type: "checkPayment" }
  | { type: "back" }
  | { type: "ok" }
  | { type: "error" }
  | { type: "reload" };

type EventMachineEmitted =
  | { type: "ok.placeTicketOrder" }
  | { type: "error.fetchTicketEvent" }
  | { type: "error.placeTicketOrder" }
  | { type: "error.fetchTicketOrder" };

export const EventMachine = setup({
  types: {} as {
    context: EventMachineContext;
    input: EventMachineInput;
    events: EventMachineEvents;
    emitted: EventMachineEmitted;
  },
  actions: {
    validate({ self }) {
      return self.send({ type: "validate.userForm" });
    },
  },
}).createMachine({
  context({ input, spawn, self }) {
    const fetchTicketEventRef = spawn(
      createFetchMachine(input.api.fetchTicketEvent.bind(input.api))
    );

    const fetchTicketOrderReceiptRef = spawn(
      createFetchMachine(input.api.fetchTicketOrderReceipt.bind(input.api))
    );

    const resendTicketOrderReceiptRef = spawn(
      createFetchMachine(input.api.resendTicketOrderReceipt.bind(input.api))
    );

    input.stripe.then((s) =>
      self.send({ type: "set.stripe", data: s ?? undefined })
    );

    fetchTicketEventRef.send({ type: "fetch", data: [{ slug: input.slug }] });
    fetchTicketEventRef.on("fetched", (r) => {
      self.send({ type: "internal.setTicketEvent", data: r.data });
      const ticketProduct = r.data.tickets;
      if (ticketProduct) {
        self.send({
          type: "internal.setTicketProduct",
          data: ticketProduct,
        });
      }
    });
    const createPaymentRef = spawn(
      createFetchMachine(input.api.createPayment.bind(input.api))
    );

    const placeTicketOrderRef = spawn(
      createFetchMachine(async (_request: PlaceTicketOrderRequest) => {
        const snap = self.getSnapshot();
        const { userForm, quantity, stripe, event, stripeElements } =
          snap.context;
        Log.debug("[placeTicketOrderRef]", { context: snap.context });

        function isRequiredMessage(f: string) {
          return `${f} is required to place a ticket order`;
        }
        assert(stripe, isRequiredMessage("context.stripe"));
        assert(stripeElements, isRequiredMessage("context.stripeElements"));

        const r = await stripeElements.submit();
        Log.debug("[EventMachine][placeTicketOrderRef][submit]", {
          result: r,
        });

        updateCustomerRef.send({
          type: "fetch",
          data: [
            {
              acceptedPrivacyPolicy: true,
              acceptedTermsOfService: true,
              email: userForm.email,
              name: `${userForm.firstName} ${userForm.lastName}`,
            },
          ],
        });

        localStorage.setItem(
          "event-machine.user-info",
          stringify(snap.context.userForm)
        );
        localStorage.setItem("event-machine.quantity", `${quantity}`);
        if (event) {
          localStorage.setItem(
            `event-machine.event.${event.slug}`,
            stringify(snap.context.event)
          );
        }

        assert(event, "context.event is required to place a ticket order");
        let ticketOrder = await input.api.placeTicketOrder({
          ticketEventId: event.id,
          order: [
            {
              quantity,
              ticketProductId: isType(event?.tickets, "paid-single")
                ? event.tickets.id ?? ""
                : "",
            },
          ],
        });

        const paymentIntentPromise = new Promise<Payment>((resolve, reject) => {
          createPaymentRef.on("fetched", (d) => resolve(d.data));
          createPaymentRef.on("error", reject);
        });
        const redirectUrl = `${window.location.protocol}//${
          window.location.host
        }${window.location.pathname}${queries({
          intent: "check-payment",
        })}`;
        createPaymentRef.send({
          type: "fetch",
          data: [
            {
              orderId: ticketOrder.order.id,
              redirectUrl: redirectUrl,
            },
          ],
        });
        const paymentIntent = await paymentIntentPromise;
        Log.debug("[EventMachine][placeTicketOrder]", { paymentIntent });
        const paymentResult = await stripe.confirmPayment({
          elements: stripeElements,
          clientSecret: paymentIntent.token,
          confirmParams: {
            return_url: `${window.location.protocol}//${window.location.host}${
              window.location.pathname
            }${queries({
              intent: "check-payment",
              ticketOrderId: ticketOrder.id,
            })}`,
          },
        });
        Log.debug("[EventMachine][placeTicketOrder]", { paymentResult });

        if (paymentResult.error) {
          Log.error(
            "[EventMachine] failed to create payment for ticket order",
            {
              ticketOrder,
              paymentResult,
            }
          );
          self.send({ type: "error" });
          return ticketOrder;
        }

        ticketOrder = await input.api.fetchTicketOrder({
          id: ticketOrder.id,
        });
        self.send({ type: "ok" });
        return ticketOrder;
      }),
      { syncSnapshot: true }
    );

    placeTicketOrderRef.on("error", (args) => {
      self.send({ type: "error" });
      Log.error("[EventMachine] failed to place ticket order", args);
    });

    const fetchTicketOrderRef = spawn(
      createFetchMachine(input.api.fetchTicketOrder.bind(input.api))
    );
    fetchTicketOrderRef.on("error", (result) =>
      Log.error("[EventMachine] failed to fetch ticket order", { result })
    );
    const searchParams = new URLSearchParams(window.location.search);
    const ticketOrderId = searchParams.get("ticketOrderId");
    if (ticketOrderId) {
      fetchTicketOrderRef.send({
        type: "fetch",
        data: [{ id: ticketOrderId }],
      });
    }
    const updateCustomerRef = spawn(
      createFetchMachine(input.api.updateCustomer.bind(input.api))
    );

    const checkPaymentRef = spawn(
      createFetchMachine(async () => {
        const snap = self.getSnapshot();
        const searchParams = new URLSearchParams(location.search);
        const stripePaymentClientSecret =
          searchParams.get("payment_intent_client_secret") ??
          snap.context.payment?.token;
        if (!stripePaymentClientSecret) return;
        if (!snap.context.stripe) return;
        const paymentIntent = await snap.context.stripe.retrievePaymentIntent(
          stripePaymentClientSecret
        );
        Log.debug("Stripe payment", { paymentIntent });
        if (
          paymentIntent?.error ||
          paymentIntent.paymentIntent.status === "requires_payment_method"
        ) {
          self.send({ type: "error" });
        } else {
          self.send({ type: "ok" });
        }
      })
    );

    const userFormItem = localStorage.getItem("event-machine.user-info");
    const userForm = userFormItem
      ? parsify(userFormItem)
      : {
          firstName: "",
          lastName: "",
          email: "",
        };

    const initialInteractions = new Set<keyof UserForm>();
    if (userForm.firstName) initialInteractions.add("firstName");
    if (userForm.lastName) initialInteractions.add("lastName");
    if (userForm.email) initialInteractions.add("email");

    const quantityItem = localStorage.getItem("event-machine.quantity");
    const quantity = quantityItem ? Number(quantityItem) : 1;

    const eventItem = localStorage.getItem(`event-machine.event.${input.slug}`);
    const event = eventItem ? parsify(eventItem) : undefined;

    self.send({ type: "validate.userForm" });

    return {
      slug: input.slug,

      quantity: quantity,
      userForm: userForm,

      event: event,

      initialInteractions: initialInteractions,
      userFormErrors: { errors: [] },

      api: {
        fetchTicketEventRef,
        placeTicketOrderRef,
        fetchTicketOrderRef,
        fetchTicketOrderReceiptRef,
        resendTicketOrderReceiptRef,
        updateCustomerRef,
        createPaymentRef,
        checkPaymentRef,
      },
    };
  },

  type: "parallel",

  on: {
    "internal.setTicketEvent": {
      actions: assign(({ event }) => ({ event: event.data })),
    },

    "set.quantity": {
      actions: assign(({ event }) => ({ quantity: Math.max(event.data, 1) })),
    },

    incrementQuantity: {
      actions: assign(({ context }) => ({
        quantity: Math.max(context.quantity + 1, 1),
      })),
    },

    decrementQuantity: {
      actions: assign(({ context }) => ({
        quantity: Math.max(context.quantity - 1, 1),
      })),
    },

    forceFirstInteraction: {
      actions: assign(({ context }) => {
        const s = new Set<UserFormInitialInteraction>(
          context.initialInteractions
        );
        s.add("email");
        s.add("firstName");
        s.add("lastName");

        return {
          initialInteractions: s,
        };
      }),
    },

    "set.payment": {
      actions: assign({ payment: unwrapData }),
    },

    "set.email": {
      actions: [
        assign(({ event, context }) => ({
          userForm: { ...context.userForm, email: event.data },
        })),
        assign(interact),
        "validate",
      ],
    },

    "set.firstName": {
      actions: [
        assign(({ event, context }) => ({
          userForm: { ...context.userForm, firstName: event.data },
        })),
        assign(interact),
        "validate",
      ],
    },

    "set.lastName": {
      actions: [
        assign(({ event, context }) => ({
          userForm: { ...context.userForm, lastName: event.data },
        })),
        assign(interact),
        "validate",
      ],
    },

    "set.stripe": {
      actions: assign({ stripe: unwrapData }),
    },

    "set.stripeElements": {
      actions: assign({ stripeElements: unwrapData }),
    },

    "goto.cart": ".ui.cart",
    "goto.detail": ".ui.detail",
    checkPayment: {
      target: ".ui.cart.checkingPayment",
      actions: ({
        context: {
          api: { checkPaymentRef },
        },
      }) => checkPaymentRef.send({ type: "fetch", data: [] }),
    },
  },

  states: {
    validation: {
      initial: "invalid",
      on: {
        "validate.userForm": {
          actions: [
            assign({
              userFormErrors: ({ context }) => {
                Log.debug("[validate.userForm]", { context });
                const parseResult = UserFormSchema.safeParse(context.userForm);
                if (parseResult.error) {
                  return z.treeifyError(parseResult.error);
                }
                return { errors: [] };
              },
            }),
            assign({
              initialInteractions: ({ context }) => {
                const initialInteractions = new Set(
                  context.initialInteractions
                );
                if (context.userForm.firstName)
                  initialInteractions.add("firstName");
                if (context.userForm.lastName)
                  initialInteractions.add("lastName");
                if (context.userForm.email) initialInteractions.add("email");
                return initialInteractions;
              },
            }),
            raise({ type: "validate.userForm.done" }),
          ],
        },

        "validate.userForm.done": [
          {
            guard: ({ context }) => {
              Log.debug("[validate.userForm.done][.invalid]", {
                userFormErrors: context.userFormErrors,
              });
              return hasErrors(context.userFormErrors);
            },
            target: ".invalid",
          },
          {
            guard: ({ context }) => {
              Log.debug("[validate.userForm.done][.valid]", {
                userFormErrors: context.userFormErrors,
              });
              return !hasErrors(context.userFormErrors);
            },
            target: ".valid",
          },
        ],
      },
      states: {
        valid: {},
        invalid: {},
        outdated: {},
      },
    },
    ui: {
      initial: "detail",
      states: {
        detail: {
          id: "detail",
        },
        cart: {
          initial: "input",
          entry: raise({ type: "validate.userForm" }),
          states: {
            input: {
              on: {
                back: "#detail",

                placeTicketOrder: {
                  target: "placingTicketOrder",
                  actions: ({
                    context: {
                      userForm,
                      event,
                      api: { placeTicketOrderRef },
                      quantity,
                    },
                  }) =>
                    placeTicketOrderRef.send({
                      type: "fetch",
                      data: [
                        {
                          customer: userForm,
                          ticketProductId:
                            (isType(event!.tickets, "paid-single") &&
                              event?.tickets.id) ||
                            "",
                          quantity: quantity,
                        } as any, // TODO: FIX,
                      ],
                    }),
                },
              },
            },

            placingTicketOrder: {
              on: {
                back: "input",
                error: {
                  actions: emit({ type: "error.placeTicketOrder" }),
                  target: "input",
                },
                ok: {
                  actions: [
                    emit({ type: "ok.placeTicketOrder" }),
                    ({
                      context: {
                        api: { checkPaymentRef },
                      },
                    }) => checkPaymentRef.send({ type: "fetch", data: [] }),
                  ],
                  target: "checkingPayment",
                },
              },
            },

            checkingPayment: {
              after: {
                10_000: "input",
              },
              entry: ({
                context: {
                  api: { checkPaymentRef },
                },
              }) => checkPaymentRef.send({ type: "fetch", data: [] }),
              on: {
                back: "input",
                error: {
                  actions: emit({ type: "error.placeTicketOrder" }),
                  target: "input",
                },
                ok: {
                  actions: emit({ type: "ok.placeTicketOrder" }),
                  target: "done",
                },
              },
            },
            done: {
              on: {
                resendReceipt: {
                  actions: [
                    async ({ context }) => {
                      await invoke(context.api.updateCustomerRef, {
                        acceptedPrivacyPolicy: true,
                        acceptedTermsOfService: true,
                        email: context.userForm.email,
                        name: `${context.userForm.firstName} ${context.userForm.lastName}`,
                      });
                      await invoke(context.api.resendTicketOrderReceiptRef, {
                        id:
                          new URLSearchParams(window.location.search).get(
                            "ticketOrderId"
                          ) ?? "",
                      });
                    },
                  ],
                },
                downloadReceipt: {
                  actions: [
                    async ({ context }) => {
                      const result = await invoke(
                        context.api.fetchTicketOrderReceiptRef,
                        {
                          id:
                            new URLSearchParams(window.location.search).get(
                              "ticketOrderId"
                            ) ?? "",
                        }
                      );
                      await downloadReceiptAndTickets(result);
                    },
                  ],
                },
              },
            },
          },
        },
      },
    },
  },
});
